import { Box, Button, useToast } from "@chakra-ui/react";
import { Form, Formik, FormikHelpers } from "formik";
import React from "react";
import { useNavigate } from "react-router-dom";

import SelectCombobox from "../../../components/elements/selectCombobox";
import { GENERIC_SAVED_MESSAGE } from "../../../constants/lang/en";
import { BUSINESS_TYPES } from "../../../constants/options";
import {
  CompanyFragmentFragment,
  useCompanyUpdateMutation,
} from "../../../graphql/graphql";
import { getRoutePath } from "../../../router";
import setServerErrors, {
  setGenericMessage,
} from "../../../utils/serverErrors";
import {
  companyBusinessTypeSchema,
  yupObject,
} from "../../../utils/validation";

interface CompanyBusinessTypeChangePagePresenterProps {
  currentCompany: CompanyFragmentFragment;
}

const CompanyBusinessTypeChangePagePresenter: React.FC<
  CompanyBusinessTypeChangePagePresenterProps
> = ({ currentCompany }) => {
  const toast = useToast();
  const navigate = useNavigate();
  const [companyUpdateMutation, { loading }] = useCompanyUpdateMutation();

  const onSubmit = async (
    data: CompanyFormData,
    { setFieldError }: FormikHelpers<CompanyFormData>
  ) => {
    try {
      const { data: serverData, errors } = await companyUpdateMutation({
        variables: { data: { ...data, name: currentCompany.name } },
      });
      if (errors) {
        setServerErrors(errors, setFieldError);
      } else if (serverData) {
        toast({
          description: GENERIC_SAVED_MESSAGE,
          status: "success",
          isClosable: true,
          position: "top",
        });
        navigate(getRoutePath("companySettings"));
        return;
      }
    } catch (error) {
      toast({
        description: setGenericMessage(error),
        status: "error",
        position: "top",
        isClosable: true,
      });
    }
  };

  return (
    <Formik
      initialValues={{
        businessType: currentCompany.businessType || "",
      }}
      validationSchema={CompanyFormValidationSchema}
      onSubmit={onSubmit}
      enableReinitialize
    >
      {() => (
        <Form noValidate>
          <SelectCombobox
            options={BUSINESS_TYPES}
            name="businessType"
            label="Select Your Business Type"
            autoFocus
          />
          <Box marginY="10">
            <Button width="full" type="submit" isLoading={loading}>
              Save
            </Button>
          </Box>
        </Form>
      )}
    </Formik>
  );
};

export default CompanyBusinessTypeChangePagePresenter;

const CompanyFormValidationSchema = yupObject().shape({
  businessType: companyBusinessTypeSchema,
});

type CompanyFormData = {
  businessType: string;
};
